import { Directive, Inject, ViewChild } from '@angular/core';
import { areEqualDeepCompare, deepCopy, DwDataPageInfo, DwDataPageResults, DwDataPageReturnInfo, DwDataRequest, DwDataSort, DwEventService, DwFilterTileChangedEventData, DwFilterTileCountsLoadedEventData, DwFilterTileEvents, DwGridColumnPreference, DwGridConfig, DwGridEvent, DwGridEventType, DwGridImplementationComponent, DwItemAttribute, DwMdDataActionResult, DwMetaDataFilterTileState, DwMetaDataService, DwMetaDataServiceToken, DwOrmDataSource, DwQueryFilterGroup, DwQueryHelper, DwRoutingService, ensureArray } from '@devwareapps/devware-cap';

import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

@Directive()
// Angular 10: use abstract directive base, not standalone
export abstract class DwAviatorGridRendererBaseComponent<TItem = any>
    extends DwGridImplementationComponent {

    public items: TItem[] = [];
    public isLoading = false;
    public hasLoaded = false;
    public errorMessage: string | null = null;

    public pageInfo: DwDataPageReturnInfo = new DwDataPageReturnInfo();

    public requestedPageInfo: DwDataPageInfo = new DwDataPageInfo();

    public sorts: DwDataSort[] = [];
    public selectedItems: TItem[] = [];

    protected ormDataSource: DwOrmDataSource | null = null;
    protected requestVersion = 0;
    protected activeLoadSub: Subscription | null = null;
    private pendingReloadHandle: ReturnType<typeof setTimeout> | null = null;
    private pendingForceRefresh = false;

    /**
     * Optional defaults for custom renderers
     */
    protected defaultPageSize = 100;
    protected resetToFirstPageOnFilterChange = true;
    protected resetSelectionOnReload = true;
    currentSearchText: string;
    searchTextAttrs: (string | DwItemAttribute)[];
    public hasFilters: boolean;
    currentFinalFilters: DwQueryFilterGroup;
    allPagesLoaded: boolean;

    constructor(
        protected dwRoutingService: DwRoutingService,
        protected adminManagerService: AdminManagerService,
        @Inject(DwMetaDataServiceToken) protected dwMetaDataService: DwMetaDataService,
        protected dwEventService: DwEventService
    ) {
        super();

        this.requestedPageInfo.pageNo = 1;
    }

    setupComplete(): void {
        if (this.data) {
            this.setData(this.data);
        }
        this.setupEvents();
        this.setupRenderer();
    }

    abstract setupRenderer(): void;

    public setupEvents() {

        this.subscriptions.push(this.dwEventService.getEvent(DwFilterTileEvents.filterTileCountsLoaded, true).subscribe((filterTileEvent) => {
            this.handleFilterTileLoadCounts(filterTileEvent?.data);
        }));

        this.subscriptions.push(this.dwEventService.getEvent(DwFilterTileEvents.filterTileChanged, true).subscribe((event) => {
            this.handleFilterTileChange(event?.data);
        }));
    } 

    selectedFilterTileState: DwMetaDataFilterTileState;

    handleFilterTileLoadCounts(filterTileEvent: DwFilterTileCountsLoadedEventData) {
        if (filterTileEvent.item != this.configContext?.itemName) {
            return;
        }

        if (this.selectedFilterTileState) {
            const selectedTileKey = this.selectedFilterTileState.filterTileConfig?.tileKey;
            const updatedTileState = filterTileEvent?.tileStates?.find(tile => tile.filterTileConfig?.tileKey === selectedTileKey);

            if (updatedTileState) {
                this.selectedFilterTileState = updatedTileState;
                this.filterTileStateChange(updatedTileState);
            }
        }
    }

    handleFilterTileChange(filterTileEvent: DwFilterTileChangedEventData) {
        if (filterTileEvent.item != this.configContext?.itemName) {
            return;
        }

        this.selectedFilterTileState = filterTileEvent.selectedTileState;

        this.filterTileStateChange(this.selectedFilterTileState);
    }

    protected abstract filterTileStateChange(filterTileState: DwMetaDataFilterTileState): void;


    public setData(data: DwOrmDataSource): void {
        super.setData(data);

        this.ormDataSource = data;
        this.syncDataSourceToSelectedView();

        this.onDataSourceChanged(data);

        this.initializePageInfo();
        this.clearRendererState();

        this.reloadData();
    }

    public updateGridConfig(gridConfig: DwGridConfig, dataSource?: any, filters?: any): void {
        this.gridConfig = gridConfig;
        this.setRendererDataSource(dataSource);

        if (filters !== undefined) {
            this.filters = filters;
        }

        this.syncDataSourceToSelectedView();

        this.onGridConfigUpdated(gridConfig);

        // Allow derived renderers to decide whether layout-only changes should skip reload later.
        this.reloadData();
    }


    textFilterSubject = new Subject<string>();

    public applyTextFilter(searchText: string): void {
        this.textFilterSubject.next(searchText);
    }

    configureTextSearch(attrs: (string | DwItemAttribute)[], delayMs: number = 100) {
        this.textFilterSubject
            .pipe(debounceTime(delayMs))
            .subscribe((searchText) => {
                this.currentSearchText = searchText;
                this.searchTextAttrs = attrs;

                this.customFiltersChanged();
            });
    }

    abstract customFiltersChanged(): void;

    protected applyCustomFilters(filterGroup: DwQueryFilterGroup) {
        if (this.currentSearchText) {
            this.addTextSearchFilter(filterGroup, this.currentSearchText, this.searchTextAttrs);
        }

        this.hasFilters = filterGroup.Filters.length > 0;

        this.currentFinalFilters = filterGroup;

        this.applyFilters(this.filters);
    }

    protected addFilter(filterGroup: DwQueryFilterGroup, value: string, attr: string | DwItemAttribute) {
        const qh = new DwQueryHelper();

        if (!value) {
            return null;
        }

        const attrName = typeof attr === 'string' ? attr : attr.fieldNameWithItemPrefix;

        const values = ensureArray(value);

        filterGroup.AddFilterAnd(qh.filters.InList(attrName, values));
    }

    protected addTextSearchFilter(filterGroup: DwQueryFilterGroup, value: string, attrs: (string | DwItemAttribute)[]) {

        if (!value) {
            return;
        }

        const qh = new DwQueryHelper();

        for (const attr of attrs) {
            const attrName = typeof attr === 'string' ? attr : attr.fieldNameWithItemPrefix;

            filterGroup.AddFilterOr(qh.filters.Contains(attrName, value));
        }
    }

    public applyFilters(filters: any): void {

        if (this.currentFinalFilters) {
            filters = {
               ...filters,
               GroupCustomFilters: deepCopy(this.currentFinalFilters)
           };
        }

        if (areEqualDeepCompare(this.filters, filters)) {
            return;
        }
        this.filters = filters || [];

        this.onFiltersApplied(this.filters);

        if (this.resetToFirstPageOnFilterChange) {
            this.requestedPageInfo.pageNo = 1;
        }

        this.reloadData();
    }

    public refreshDataSource(): void {
        this.reloadData(true);
        this.raiseRefreshEvent();
    }

    /**
     * Public helpers for paging UI
     */
    public goToPage(pageNumber: number): void {
        const normalizedPage = Math.max(1, pageNumber || 1);

        if (this.pageInfo.currentPageNo === normalizedPage && this.hasLoaded) {
            return;
        }

        this.loadPage(normalizedPage);
    }

    public nextPage(): void {
        if (!this.canGoToNextPage()) {
            return;
        }

        this.loadPage((this.pageInfo.currentPageNo || 1) + 1);
    }

    public previousPage(): void {
        if (!this.canGoToPreviousPage()) {
            return;
        }

        this.loadPage((this.pageInfo.currentPageNo || 1) - 1);
    }

    public changePageSize(pageSize: number): void {
        const normalizedSize = Math.max(1, pageSize || this.defaultPageSize);

        if (this.pageInfo.currentPageNo === normalizedSize && this.hasLoaded) {
            return;
        }

        this.requestedPageInfo.pageSize = normalizedSize;
        this.requestedPageInfo.pageNo = 1;

        this.reloadData();
    }

    /**
     * Public helpers for selection
     */
    public setSelectedItems(items: TItem[]): void {
        this.selectedItems = items || [];
        this.onSelectionChanged(this.selectedItems);
    }

    public toggleSelectedItem(item: TItem, comparer?: (a: TItem, b: TItem) => boolean): void {
        const equals = comparer || this.areItemsEqual.bind(this);
        const existingIndex = this.selectedItems.findIndex(x => equals(x, item));

        if (existingIndex >= 0) {
            this.selectedItems = [
                ...this.selectedItems.slice(0, existingIndex),
                ...this.selectedItems.slice(existingIndex + 1)
            ];
        } else {
            this.selectedItems = [...this.selectedItems, item];
        }

        this.onSelectionChanged(this.selectedItems);
    }

    public clearSelection(): void {
        if (!this.selectedItems.length) {
            return;
        }

        this.selectedItems = [];
        this.onSelectionChanged(this.selectedItems);
    }

    /**
     * Public helper for sort UI
     */
    public setSorts(sorts: DwDataSort[]): void {
        this.sorts = sorts || [];
        this.requestedPageInfo.pageNo = 1;
        this.onSortChanged(this.sorts);
        this.reloadData();
    }

    public canGoToPreviousPage(): boolean {
        return (this.pageInfo.currentPageNo || 1) > 1;
    }

    public canGoToNextPage(): boolean {
        if (!this.hasLoaded) {
            return false;
        }

        if (this.allPagesLoaded) {
            return false;
        }

        // Prefer total count when available
        if (this.pageInfo.totalPageCount != null && this.pageInfo.currentPageSize > 0) {
            return (this.pageInfo.currentPageNo * this.pageInfo.currentPageSize) < this.pageInfo.totalRecordCount;
        }

        // Fallback: if the last page returned fewer than page size, probably end
        return this.items.length >= (this.pageInfo.currentPageSize || this.defaultPageSize);
    }

    public get totalCount(): number {
        return this.pageInfo.totalRecordCount || 0;
    }

    public get pageNumber(): number {
        return this.pageInfo.currentPageNo || 1;
    }

    public get pageSize(): number {
        return this.pageInfo.currentPageSize || this.defaultPageSize;
    }

    protected initializePageInfo(): void {
        this.requestedPageInfo = new DwDataPageInfo();
        this.requestedPageInfo.pageNo = 1;
        this.requestedPageInfo.pageSize = this.resolveInitialPageSize();
    }

    protected reloadData(forceRefresh: boolean = false): void {
        this.pendingForceRefresh = this.pendingForceRefresh || forceRefresh;

        if (this.pendingReloadHandle !== null) {
            return;
        }

        // CAP supplies the data source, grid configuration, filters, and renderer
        // defaults through separate lifecycle callbacks. Coalesce reloads raised in
        // the same browser turn so initialization results in one request.
        this.pendingReloadHandle = setTimeout(() => {
            this.pendingReloadHandle = null;

            const shouldForceRefresh = this.pendingForceRefresh;
            this.pendingForceRefresh = false;

            this.loadPage(this.requestedPageInfo.pageNo || 1, shouldForceRefresh);
        }, 0);
    }

    protected loadPage(pageNumber: number, forceRefresh: boolean = false): void {
        this.cancelPendingReload();

        if (!this.ormDataSource) {
            this.handleLoadError('No ORM data source is configured for this renderer.');
            return;
        }

        this.cancelActiveLoad();

        this.isLoading = true;
        this.errorMessage = null;

        if (this.resetSelectionOnReload) {
            this.selectedItems = [];
        }

        this.requestedPageInfo.pageNo = Math.max(1, pageNumber || 1);
        this.requestedPageInfo.pageSize = this.requestedPageInfo.pageSize || this.defaultPageSize;

        if (this.requestedPageInfo.pageNo == 1) {
            this.allPagesLoaded = false;
        }

        const request = this.buildDataRequest();
        const currentVersion = ++this.requestVersion;

        this.beforeLoad(request, forceRefresh);

        this.activeLoadSub = this.ormDataSource.requestData(request).subscribe({
            next: result => {
                if (currentVersion !== this.requestVersion) {
                    return;
                }

                const newItems = this.mapResults(result && result.data ? result.data : []);
                if (result.pageInfo?.currentPageNo === 1) {
                    this.items = newItems;
                } else {
                    this.items = [...(this.items || []), ...newItems];
                }

                if (result.data.length < result.pageInfo?.currentPageSize) {
                    // all pages loaded
                    this.allPagesLoaded = true;
                }

                this.updatePageInfoFromResult(result);
                this.isLoading = false;
                this.hasLoaded = true;

                this.afterLoad(this.items, result);
            },
            error: error => {
                if (currentVersion !== this.requestVersion) {
                    return;
                }

                this.handleLoadFailure(error);
            }
        });
    }

    protected buildDataRequest(): DwDataRequest {
        const request = new DwDataRequest();

        request.pageInfo = new DwDataPageInfo();
        request.pageInfo.pageNo = this.requestedPageInfo.pageNo || 1;
        request.pageInfo.pageSize = this.requestedPageInfo.pageSize || this.defaultPageSize;

        request.filters = this.filters;
        request.sorting = this.getEffectiveSorts();

        return request;
    }

    protected syncDataSourceToSelectedView(): void {
        const selectedView = (this.gridConfig as any)?.selectedView;
        const selectedViewQuery = selectedView?.query || (this.gridConfig as any)?.queryMetaData?.DataQuery;

        if (!this.ormDataSource || !selectedViewQuery?.RootTable) {
            return;
        }

        this.ormDataSource.item = new DwQueryHelper().cloneQuery(selectedViewQuery);
        this.ormDataSource.uniqueIdentifier = `${selectedView?.gridViewPreferences?.metaDataQueryMeaning || 'selected-view'}_${selectedView?.key || ''}`;
    }

    private setRendererDataSource(dataSource?: any): void {
        if (!dataSource) {
            return;
        }

        this.data = dataSource;
        this.dataSource = dataSource;

        if (dataSource instanceof DwOrmDataSource) {
            this.ormDataSource = dataSource;
        }
    }

    protected getEffectiveSorts(): DwDataSort[] {
        return this.sorts || [];
    }

    protected mapResults(results: any[]): TItem[] {
        return (results || []) as TItem[];
    }

    protected updatePageInfoFromResult(result: DwDataPageResults): void {
        if (!result) {
            return;
        }

        if (result.pageInfo) {
            this.pageInfo = result.pageInfo;
            return;
        }

        // This shouldn't happen
        console.warn('Data result is missing page info. Paging UI may not work correctly.', result);
    }

    protected handleLoadFailure(error: any): void {
        this.isLoading = false;
        this.hasLoaded = true;
        this.items = [];
        this.errorMessage = this.getLoadErrorMessage(error);

        this.onLoadError(error);
    }

    protected handleLoadError(message: string): void {
        this.isLoading = false;
        this.hasLoaded = true;
        this.items = [];
        this.errorMessage = message;
    }

    protected getLoadErrorMessage(error: any): string {
        return 'An error occurred while loading data.';
    }

    protected clearRendererState(): void {
        this.items = [];
        this.selectedItems = [];
        this.sorts = [];
        this.isLoading = false;
        this.hasLoaded = false;
        this.errorMessage = null;
    }

    protected cancelActiveLoad(): void {
        if (this.activeLoadSub) {
            this.activeLoadSub.unsubscribe();
            this.activeLoadSub = null;
        }
    }

    private cancelPendingReload(): void {
        if (this.pendingReloadHandle === null) {
            return;
        }

        clearTimeout(this.pendingReloadHandle);
        this.pendingReloadHandle = null;
        this.pendingForceRefresh = false;
    }

    protected raiseRefreshEvent(): void {
        this.onGridEvent({ eventType: DwGridEventType.refresh, dwBrowserEvent: null });
    }

    protected resolveInitialPageSize(): number {
        return this.defaultPageSize;
    }

    protected areItemsEqual(a: TItem, b: TItem): boolean {
        return a === b;
    }

    getSelectedRows(): any[] {
        return this.selectedItems || [];
    }

    getEditedRows(): any[] {
        return [];
    }

    unselectAllRows(): void {
        this.selectedItems = [];
    }

    selectAllRows(): void {

    }

    getColumnState(): DwGridColumnPreference[] {
        return [];
    }


    processDataActionResult(dataActionResult: DwMdDataActionResult) {
        // ignore, doesn't apply
    }


    /**
     * Extension hooks for final bespoke renderers
     */
    protected onDataSourceChanged(data: DwOrmDataSource): void { }
    protected onGridConfigUpdated(gridConfig: DwGridConfig): void { }
    protected onFiltersApplied(filters: any): void { }
    protected onSortChanged(sorts: DwDataSort[]): void { }
    protected onSelectionChanged(items: TItem[]): void { }
    protected beforeLoad(request: DwDataRequest, forceRefresh: boolean): void { }
    protected afterLoad(items: TItem[], result: any): void { }
    protected onLoadError(error: any): void { }

    public ngOnDestroy(): void {
        this.cancelPendingReload();
        this.cancelActiveLoad();
        super.ngOnDestroy();
    }
}
